import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Info, PieChartIcon } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip } from 'recharts';

interface CategoryData {
  category: string;
  value: number;
  count: number;
  color: string;
}

interface AssetsByCategoryCardProps {
  readonly data: CategoryData[];
  readonly className?: string;
}

interface CustomTooltipProps {
  readonly active?: boolean;
  readonly payload?: Array<{ payload: { name: string; displayValue: string; percentage: number } }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border">
        <p className="font-semibold">{data.name}</p>
        <p className="text-sm">{data.displayValue}</p>
        <p className="text-xs text-muted-foreground">
          {data.percentage.toFixed(1)}%
        </p>
      </div>
    );
  }
  return null;
};

interface CustomLabelProps {
  readonly cx?: number;
  readonly cy?: number;
  readonly midAngle?: number;
  readonly innerRadius?: number;
  readonly outerRadius?: number;
  readonly percent?: number;
}

const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: CustomLabelProps) => {
  if (cx === undefined || cy === undefined || midAngle === undefined || 
      innerRadius === undefined || outerRadius === undefined || percent === undefined) {
    return null;
  }
  
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const percentage = percent * 100;

  if (percentage < 5) return null;

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-xs font-semibold"
    >
      {`${percentage.toFixed(0)}%`}
    </text>
  );
};

export function AssetsByCategoryCard({ data = [], className }: AssetsByCategoryCardProps): React.ReactElement {
  const navigate = useNavigate();
  const [metric, setMetric] = useState<'count' | 'value'>('count');

  // Check if data array has items AND at least one category has a non-zero count or value
  // This prevents rendering a "stick" (thin line) when all values are 0
  const hasData = Array.isArray(data) && data.length > 0 && 
    data.some(item => item.count > 0 || item.value > 0);

  // Calculate total value based on selected metric
  const totalValue = data.reduce((sum, d) => sum + (metric === 'count' ? d.count : d.value), 0);
  
  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Prepare chart data with percentages
  const chartData = data.map(item => {
    const currentValue = metric === 'count' ? item.count : item.value;
    const percentage = totalValue > 0 ? (currentValue / totalValue) * 100 : 0;
    return {
      name: item.category,
      value: currentValue,
      displayValue: metric === 'count' ? `${item.count} assets` : formatCurrency(item.value),
      percentage,
      color: item.color,
    };
  });

  // Handle legend item click to navigate to filtered assets page
  const handleLegendClick = (category: string) => {
    navigate(`/assets?category=${encodeURIComponent(category)}`);
  };

  if (!hasData) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full min-h-[400px] w-full flex flex-col', className)}>
        <CardHeader className="relative">
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Assets by Category</CardTitle>
              <CardDescription>Asset value distribution across categories</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="text-sm text-muted-foreground">No category data available.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full min-h-[400px] min-w-0 w-full flex flex-col', className)}>
      <CardHeader className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Assets by Category</CardTitle>
            <CardDescription>
              {metric === 'count' ? 'Asset count distribution across categories' : 'Asset value distribution across categories'}
            </CardDescription>
          </div>
          <div className="flex gap-1 items-center">
            <Select value={metric} onValueChange={(value: 'count' | 'value') => setMetric(value)}>
              <SelectTrigger className="h-7 w-[100px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="count">Count</SelectItem>
                <SelectItem value="value">Value</SelectItem>
              </SelectContent>
            </Select>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80">
                    <Info className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    This chart shows the total asset {metric === 'count' ? 'count' : 'value'} distributed across different categories. 
                    Click on a category in the legend to view those assets.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <PieChartIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-4">
          {/* Total Value Display - Same style as Assets by Location and Status */}
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-bold">
              {metric === 'count' 
                ? totalValue 
                : formatCurrency(totalValue)
              }
            </div>
            <div className="text-sm text-muted-foreground">
              {metric === 'count' ? 'Total Assets' : 'Total Asset Value'}
            </div>
          </div>

          {/* Pie Chart */}
          <div className="flex justify-center items-center h-[240px] w-full">
            <PieChart width={220} height={220}>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={CustomLabel}
                outerRadius={90}
                dataKey="value"
              >
                {chartData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomTooltip />} />
            </PieChart>
          </div>

          {/* Legend */}
          <div className="space-y-2">
            {chartData.map((item) => (
              <button
                key={item.name}
                type="button"
                onClick={() => handleLegendClick(item.name)}
                className="flex items-center justify-between text-sm w-full hover:bg-muted/50 p-2 rounded-md transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="truncate font-medium">{item.name}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-semibold">{item.displayValue}</span>
                  <span className="text-xs text-muted-foreground">
                    ({item.percentage.toFixed(1)}%)
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
